import { v4 as uuid } from 'uuid';
import { createSpinWheelProgress, getSpinWheelProgress, updateSpinWheelProgress } from '../../API/api';
import {
  applyAppOpenToProgress,
  createDefaultSpinWheelProgress,
  getLocalDateKey,
  isSpinWheelEligible,
  normalizeSpinWheelProgress,
} from './spinWheelProgress';

const SESSION_ID_STORAGE_KEY = 'sessionId';

export const getOrCreateSessionId = () => {
  const storedSessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);

  if (storedSessionId && storedSessionId !== 'undefined' && storedSessionId !== 'null') {
    return storedSessionId;
  }

  const sessionId = `user${uuid()}`;
  localStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);

  return sessionId;
};

const readResponseJson = async (response) => {
  const responseText = typeof response?.text === 'function' ? await response.text() : '';

  if (!responseText.trim()) {
    return {};
  }

  return JSON.parse(responseText);
};

const assertOkResponse = (response, actionName) => {
  if (!response?.ok) {
    throw new Error(`${actionName} failed with ${response?.status || 'unknown'} status`);
  }
};

const areProgressRecordsEqual = (firstProgress, secondProgress) =>
  JSON.stringify(firstProgress) === JSON.stringify(secondProgress);

const createProgressRecord = async (sessionId) => {
  const progress = createDefaultSpinWheelProgress(sessionId, getLocalDateKey());
  const response = await createSpinWheelProgress(progress);

  assertOkResponse(response, 'Creating spin wheel progress');

  const createdProgress = await readResponseJson(response);

  return normalizeSpinWheelProgress(Object.keys(createdProgress).length > 0 ? createdProgress : progress, sessionId);
};

export const loadProgressRecordWithStatus = async (sessionId) => {
  const response = await getSpinWheelProgress(sessionId);

  if (response?.status === 404) {
    return {
      becameEligible: false,
      progress: await createProgressRecord(sessionId),
    };
  }

  assertOkResponse(response, 'Fetching spin wheel progress');

  const progress = await readResponseJson(response);
  const normalizedProgress = normalizeSpinWheelProgress(progress, sessionId);
  const openedProgress = applyAppOpenToProgress(normalizedProgress, getLocalDateKey());
  const becameEligible = !isSpinWheelEligible(normalizedProgress) && isSpinWheelEligible(openedProgress);

  if (areProgressRecordsEqual(openedProgress, normalizedProgress)) {
    return { becameEligible, progress: openedProgress };
  }

  const updateResponse = await updateSpinWheelProgress(sessionId, openedProgress);
  assertOkResponse(updateResponse, 'Updating spin wheel progress');

  const updatedProgress = await readResponseJson(updateResponse);

  return {
    becameEligible,
    progress: normalizeSpinWheelProgress(
      Object.keys(updatedProgress).length > 0 ? updatedProgress : openedProgress,
      sessionId
    ),
  };
};

export const loadProgressRecord = async (sessionId) => {
  const { progress } = await loadProgressRecordWithStatus(sessionId);

  return progress;
};

export const saveProgressRecord = async (progress) => {
  const response = await updateSpinWheelProgress(progress.session_id, progress);
  assertOkResponse(response, 'Saving spin wheel progress');

  const updatedProgress = await readResponseJson(response);

  return normalizeSpinWheelProgress(
    Object.keys(updatedProgress).length > 0 ? updatedProgress : progress,
    progress.session_id
  );
};
